import React, { useState, useEffect } from 'react';
import { 
  BoltIcon, 
  SignalIcon,
  CloudIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { monitoringApi, EquipmentList, EquipmentDetails } from '../../services/monitoringApi';

const EquipmentMonitoring: React.FC = () => {
  const [equipmentList, setEquipmentList] = useState<EquipmentList | null>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<string | null>(null);
  const [equipmentDetails, setEquipmentDetails] = useState<EquipmentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailsLoading, setDetailsLoading] = useState(false);

  useEffect(() => {
    fetchEquipmentList();
  }, []);

  useEffect(() => {
    if (selectedEquipment) {
      fetchEquipmentDetails(selectedEquipment);
    }
  }, [selectedEquipment]);

  const fetchEquipmentList = async () => {
    try {
      setLoading(true);
      const data = await monitoringApi.getEquipmentList();
      setEquipmentList(data);
    } catch (error) {
      console.error('Error fetching equipment list:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEquipmentDetails = async (equipmentId: string) => {
    try {
      setDetailsLoading(true);
      const data = await monitoringApi.getEquipmentParameters(equipmentId);
      setEquipmentDetails(data);
    } catch (error) {
      console.error('Error fetching equipment details:', error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'idle':
        return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      case 'offline':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      default:
        return <XCircleIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'idle':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'offline':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold gradient-neural">Equipment Monitoring</h1>
          <p className="text-slate-400 mt-2">Real-time equipment status and parameters</p>
        </div>
        
        <button
          onClick={fetchEquipmentList}
          className="ai-btn-primary"
        >
          Refresh Data
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <BoltIcon className="w-6 h-6 text-blue-500" />
              <h2 className="text-lg font-semibold">Inverters ({equipmentList?.inverters.length || 0})</h2>
            </div>
            
            <div className="space-y-2">
              {equipmentList?.inverters.map((inverter) => (
                <div
                  key={inverter.id}
                  onClick={() => setSelectedEquipment(inverter.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${
                    selectedEquipment === inverter.id 
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">{inverter.name}</div>
                      <div className="text-xs text-gray-500">
                        {inverter.current_power ? `${inverter.current_power.toFixed(1)} kW` : 'No data'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(inverter.status)}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(inverter.status)}`}>
                        {inverter.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <SignalIcon className="w-6 h-6 text-green-500" />
              <h2 className="text-lg font-semibold">Meters ({equipmentList?.meters.length || 0})</h2>
            </div>
            
            <div className="space-y-2">
              {equipmentList?.meters.map((meter) => (
                <div
                  key={meter.id}
                  onClick={() => setSelectedEquipment(meter.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${
                    selectedEquipment === meter.id 
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">{meter.name}</div>
                      <div className="text-xs text-gray-500">Energy Meter</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(meter.status)}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(meter.status)}`}>
                        {meter.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <CloudIcon className="w-6 h-6 text-purple-500" />
              <h2 className="text-lg font-semibold">Weather Stations ({equipmentList?.weather_stations.length || 0})</h2>
            </div>
            
            <div className="space-y-2">
              {equipmentList?.weather_stations.map((station) => (
                <div
                  key={station.id}
                  onClick={() => setSelectedEquipment(station.id)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md ${
                    selectedEquipment === station.id 
                      ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
                      : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-sm">{station.name}</div>
                      <div className="text-xs text-gray-500">Environmental Data</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusIcon(station.status)}
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(station.status)}`}>
                        {station.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2">
          {selectedEquipment ? (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold">Equipment Details: {selectedEquipment}</h2>
                {detailsLoading && (
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-violet-500"></div>
                )}
              </div>

              {equipmentDetails && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {equipmentDetails.parameters.map((param, index) => (
                    <div key={index} className="p-4 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium text-sm">{param.name}</div>
                        <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                          param.quality === 'good' 
                            ? 'bg-green-100 text-green-800' 
                            : param.quality === 'bad'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {param.quality}
                        </div>
                      </div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        {param.value.toFixed(2)} {param.unit}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Updated: {new Date(param.timestamp).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-12 border border-gray-200 dark:border-gray-700 text-center">
              <div className="text-gray-400 mb-4">
                <BoltIcon className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Select Equipment
              </h3>
              <p className="text-gray-500">
                Choose an equipment from the list to view its real-time parameters and status.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EquipmentMonitoring;